import React, { useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Box, Button, IconButton, Image, Skeleton, Text } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, ArrowForward, Place, WhatsApp } from '@mui/icons-material';

import ProductGrid from '../product/ProductGrid';
import { categoryImage } from '../../hooks/useCatalog';
import { STORE, whatsappLink } from '../../config/store';

// Home page sections, top to bottom (design-system.md §5.1). Banners run edge to edge with no gaps.

// Big white condensed headline + underlined "CTA →", bottom-left over a photo
export const Overlay = ({ title, ctas = [], size = 'xl' }) => (
    <Box position='absolute' inset={0} display='flex' flexDirection='column' justifyContent='flex-end' px={{ base: '14px', md: '60px' }} pb={{ base: '16px', md: '44px' }} bgGradient='linear(to-t, blackAlpha.500, transparent 45%)' color='raano.white' pointerEvents='none' >
        {title &&
            <Text as='h2' fontWeight={700} textTransform='uppercase' lineHeight={0.95} sx={{ fontStretch: '75%' }}
                fontSize={size === 'xl' ? { base: '26px', md: '72px' } : { base: '22px', md: '52px' }} >{title}</Text>}
        <Box display='flex' gap={{ base: '16px', md: '28px' }} mt={{ base: '8px', md: '14px' }} pointerEvents='auto' >
            {ctas.map(c => (
                <Text key={c.label} as='button' textStyle='bannerCta' fontSize={{ base: '12px', md: '18px' }} display='inline-flex' alignItems='center' gap='6px' borderBottom='2px solid' borderColor='raano.white' pb='2px' onClick={c.onClick} >
                    {c.label} <ArrowForward sx={{ fontSize: 18 }} />
                </Text>
            ))}
        </Box>
    </Box>
);

const Arrow = ({ dir, onClick }) => (
    <IconButton
        aria-label={dir === 'prev' ? 'Previous slide' : 'Next slide'}
        icon={dir === 'prev' ? <ChevronLeft /> : <ChevronRight />}
        onClick={onClick}
        display={{ base: 'none', md: 'inline-flex' }}
        position='absolute' top='50%' transform='translateY(-50%)' zIndex={2}
        {...(dir === 'prev' ? { left: '20px' } : { right: '20px' })}
        width='44px' height='44px' minWidth='44px' borderRadius='full' bg='raano.white' color='raano.black' border='1px solid' borderColor='raano.border'
        _hover={{ bg: 'raano.white', transform: 'translateY(-50%) scale(1.05)' }}
    />
);

// 1 — Hero carousel: 16:9 desktop, 4:3 phone, dots, autoplay
export const HeroCarousel = ({ images, copy, onShop }) => {
    if (!images.length) return <Skeleton sx={{ aspectRatio: { base: '4 / 3', md: '16 / 9' } }} />;
    const settings = {
        dots: true, arrows: true, infinite: images.length > 1, autoplay: true, autoplaySpeed: 5000, speed: 600, pauseOnHover: true,
        prevArrow: <Arrow dir='prev' />, nextArrow: <Arrow dir='next' />,
        appendDots: dots => <Box as='ul' bottom={{ base: '8px !important', md: '18px !important' }} sx={{ 'li button:before': { color: '#fff !important', fontSize: '10px', opacity: '0.6' }, 'li.slick-active button:before': { opacity: '1 !important' } }} >{dots}</Box>,
    };
    return (
        <Box position='relative' sx={{ '.slick-slider': { marginBottom: 0 } }} >
            <Slider {...settings} >
                {images.map((img, i) => {
                    const c = copy[i % copy.length] || {};
                    return (
                        <Box key={img._id} position='relative' >
                            <Box bg='raano.imageBg' sx={{ aspectRatio: { base: '4 / 3', md: '16 / 9' } }} >
                                <Image src={img.url} alt={c.title || 'Raano'} width='100%' height='100%' objectFit='cover' loading={i ? 'lazy' : 'eager'} fetchpriority={i ? undefined : 'high'} />
                            </Box>
                            <Overlay title={c.title} ctas={[{ label: c.cta || 'Shop now', onClick: onShop }]} />
                        </Box>
                    );
                })}
            </Slider>
        </Box>
    );
};

// 2–3 — Dual category tiles: ~1.7:1 desktop, 2:3 phone
export const CategoryTiles = ({ categories, products, openCategory }) => {
    const tiles = categories.filter(c => categoryImage(products, c._id)).slice(0, 4);
    if (!tiles.length) return null;
    return (
        <Box display='grid' gridTemplateColumns='1fr 1fr' >
            {tiles.map(c => (
                <Box key={c._id} as='button' position='relative' overflow='hidden' bg='raano.imageBg' role='group' onClick={() => openCategory(c._id)} sx={{ aspectRatio: { base: '2 / 3', md: '720 / 423' } }} >
                    <Image src={categoryImage(products, c._id)} alt={c.name} width='100%' height='100%' objectFit='cover' objectPosition='center 25%' loading='lazy' transition='transform .5s' _groupHover={{ transform: 'scale(1.03)' }} />
                    <Box position='absolute' inset={0} bgGradient='linear(to-t, blackAlpha.500, transparent 40%)' />
                    <Text position='absolute' left={{ base: '12px', md: '40px' }} bottom={{ base: '14px', md: '34px' }} color='raano.white' textStyle='bannerCta' fontSize={{ base: '13px', md: '22px' }} display='inline-flex' alignItems='center' gap='6px' borderBottom='2px solid' pb='2px' >
                        {c.name} <ArrowForward sx={{ fontSize: 20 }} />
                    </Text>
                </Box>
            ))}
        </Box>
    );
};

// 4–5 — Full-width editorial banner: 16:9 desktop, 2:3 phone
export const EditorialBanner = ({ image, title, ctas }) => image ? (
    <Box position='relative' overflow='hidden' bg='raano.imageBg' sx={{ aspectRatio: { base: '2 / 3', md: '16 / 9' } }} >
        <Image src={image} alt={title} width='100%' height='100%' objectFit='cover' loading='lazy' />
        <Overlay title={title} ctas={ctas} size='lg' />
    </Box>
) : null;

// 6 — Tabbed product grid
export const TabbedProducts = ({ categories, products, loading, onViewAll }) => {
    const [tab, setTab] = useState('new');
    const tabs = [{ _id: 'new', name: 'New In' }, ...categories];
    const list = tab === 'new' ? [...products].reverse().slice(0, 12) : products.filter(p => p.category === tab).slice(0, 12);
    return (
        <Box px={{ base: '12px', md: '20px' }} pt={{ base: '28px', md: '48px' }} pb={{ base: '24px', md: '40px' }} >
            <Box display='flex' alignItems='center' justifyContent='space-between' gap='16px' mb={{ base: '16px', md: '24px' }} >
                <Text as='h2' fontWeight={700} textTransform='uppercase' sx={{ fontStretch: '75%' }} fontSize={{ base: '24px', md: '36px' }} lineHeight={1} >Shop the latest</Text>
                <Text as='button' fontSize='14px' fontWeight={500} textDecoration='underline' flexShrink={0} onClick={() => onViewAll(tab === 'new' ? null : tab)} >View all</Text>
            </Box>
            <Box role='tablist' display='flex' gap='8px' overflowX='auto' mb={{ base: '16px', md: '24px' }} sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }} >
                {tabs.map(t => (
                    <Box key={t._id} as='button' role='tab' aria-selected={tab === t._id} flexShrink={0} height='36px' px='16px' borderRadius='full' border='1px solid'
                        borderColor={tab === t._id ? 'raano.black' : 'raano.border'} bg={tab === t._id ? 'raano.black' : 'raano.white'} color={tab === t._id ? 'raano.white' : 'raano.black'}
                        fontSize='14px' transition='all .125s' _hover={{ borderColor: 'raano.black' }} onClick={() => setTab(t._id)} >{t.name}</Box>
                ))}
            </Box>
            <ProductGrid products={list} loading={loading} skeletons={8} />
            <Box display='flex' justifyContent='center' mt={{ base: '24px', md: '36px' }} >
                <Button variant='wipe' height='48px' px='48px' fontSize='16px' onClick={() => onViewAll(tab === 'new' ? null : tab)} >View all products</Button>
            </Box>
        </Box>
    );
};

// Raano's own store block, kept from the old home page and restyled
export const VisitStore = () => (
    <Box display='flex' flexDirection={{ base: 'column', md: 'row' }} mx={{ base: '12px', md: '20px' }} mb={{ base: '8px', md: '16px' }} border='1px solid' borderColor='raano.border' >
        <Box p={{ base: '20px', md: '40px' }} width={{ base: '100%', md: '40%' }} display='flex' flexDirection='column' justifyContent='center' >
            <Text fontSize='12px' fontWeight={700} letterSpacing='0.12em' textTransform='uppercase' color='raano.muted' >Visit our store</Text>
            <Text as='h2' mt='8px' fontWeight={700} textTransform='uppercase' sx={{ fontStretch: '75%' }} fontSize={{ base: '28px', md: '40px' }} lineHeight={1} >{STORE.name}</Text>
            <Text mt='12px' fontSize='15px' color='raano.muted' >Try the fit in person, or message us on WhatsApp and we'll help you pick the right size.</Text>
            <Box display='flex' gap='10px' mt='20px' flexWrap='wrap' >
                <Button as='a' href={STORE.mapsUrl} target='_blank' rel='noreferrer' variant='ink' height='44px' px='20px' fontSize='15px' leftIcon={<Place fontSize='small' />} >Get directions</Button>
                <Button as='a' href={whatsappLink('Hi Raano!')} target='_blank' rel='noreferrer' variant='wipe' height='44px' px='20px' fontSize='15px' leftIcon={<WhatsApp fontSize='small' />} >WhatsApp</Button>
            </Box>
        </Box>
        <Box flex={1} minHeight={{ base: '240px', md: '360px' }} bg='raano.surface' >
            <iframe title={`${STORE.name} on Google Maps`} src={STORE.mapsEmbedUrl} width='100%' height='100%' style={{ border: 0, minHeight: 'inherit', display: 'block', filter: 'grayscale(1)' }} loading='lazy' referrerPolicy='strict-origin-when-cross-origin' />
        </Box>
    </Box>
);
